import { supabase } from '@/integrations/supabase/client';

export type ExperimentStatName =
  | 'Completed tests'
  | 'Completed experiments'
  | 'Reviews collected'
  | 'Total participants'
  | 'Average rating'
  | 'Would recommend product'
  | 'Video submissions'
  | 'Social media posts';

export type ExperimentStatsMap = Record<string, { value: string; description: string }>;

// Reusable function to fetch experiment stats from Metabase (card id 67)
export const fetchExperimentStats = async (
  experimentIds: string[]
): Promise<ExperimentStatsMap> => {
  // Default zeros if no experiments
  if (!experimentIds || experimentIds.length === 0) {
    return {
      'Completed tests': { value: '0', description: 'Number of completed tests' },
      'Completed experiments': { value: '0', description: 'Number of completed experiments' },
      'Total participants': { value: '0', description: 'Total number of participants' },
      'Average rating': { value: '0', description: 'Average rating from participants' },
      'Reviews collected': { value: '0', description: 'Number of reviews collected' },
      'Would recommend product': { value: '0', description: 'Participants who would recommend the product' },
      'Video submissions': { value: '0', description: 'Number of video submissions' },
      'Social media posts': { value: '0', description: 'Number of social media posts' },
    };
  }

  const { data, error } = await supabase.functions.invoke('metabase-query/67', {
    body: {
      parameters: [
        {
          type: 'text',
          target: ['variable', ['template-tag', 'experiment_ids']],
          value: experimentIds.join(','),
        },
      ],
      format_rows: true,
      pivot_results: false,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to fetch Metabase stats');
  }

  const rows = Array.isArray(data) ? (data as any[]) : [];
  const result: ExperimentStatsMap = {};

  for (const row of rows) {
    const key = (row?.statistic ?? row?.['statistic']) as string | undefined;
    const value = String(row?.value ?? row?.['value'] ?? '0');
    const description = String(row?.description ?? row?.['description'] ?? '');
    if (key) result[key] = { value, description };
  }

  // Ensure all expected keys exist
  const expected: ExperimentStatName[] = [
    'Completed tests',
    'Completed experiments',
    'Total participants',
    'Average rating',
    'Reviews collected',
    'Would recommend product',
    'Video submissions',
    'Social media posts',
  ];
  expected.forEach((k) => {
    if (result[k] === undefined) result[k] = { value: '0', description: '' };
  });

  return result;
};
